/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState } from 'react';
import SubframeCore from "@subframe/core";
import moment from "moment";
import MessageComponent from "../MessageComponent";
import { DialogLayout } from "@/subframe/layouts/DialogLayout";
import { IconButton } from "@/subframe/components/IconButton";


// SystemUserMessage interface
interface SystemUserMessage {
  message_id?: string;
  id?: string;
  system_user?: string;
  created_at: string;
  status?: "sent" | "delivered" | "read" | "passed" | "failed";
  media_url?: string;
  system_user_payload?: any;
  send_by?: string;
  type?: string;
}

// SystemUserProps interface
interface SystemUserProps {
  message: SystemUserMessage;
  scrollRef: React.RefObject<HTMLDivElement>;
}

const SystemUser: React.FC<SystemUserProps> = ({ message, scrollRef }) => {
  const [showPreview, setShowPreview] = useState(false);
  const type: any = message.system_user_payload?.type;
  const template: any = message.system_user_payload?.template;
  const headerComponent = template?.components?.find((c: any) => c.type === "header");

  // Helper function to get media URL from different sources
  const getMediaUrl = () => {
    return (
      message.media_url ||
      message.system_user_payload?.image?.link ||
      message.system_user_payload?.video?.link ||
      message.system_user_payload?.document?.link ||
      message.system_user_payload?.audio?.link ||
      headerComponent?.parameters?.[0]?.image?.link ||
      headerComponent?.parameters?.[0]?.video?.link ||
      headerComponent?.parameters?.[0]?.document?.link
    );
  };

  const handlePreviewClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setShowPreview(true);
  };

  const getLocationEmbedUrl = (url: string) => {
    const separator = url.includes("?") ? "&" : "?";
    return `${url}${separator}z=15&output=embed`;
  };

  // Helper function to render preview content
  const renderPreview = () => {
    const mediaUrl = getMediaUrl();
    if (!mediaUrl) return null;

    if (type === 'image' || headerComponent?.parameters?.[0]?.type === 'image') {
      return (
        <img
          className="max-h-96 w-auto flex-none rounded-md shadow-lg"
          src={mediaUrl}
          alt="Preview"
        />
      );
    }

    if (type === 'video' || headerComponent?.parameters?.[0]?.type === 'video') {
      return (
        <video
          className="max-h-96 w-auto flex-none rounded-md shadow-lg"
          controls
        >
          <source src={mediaUrl} type="video/mp4" />
          Your browser does not support the video tag.
        </video>
      );
    }

    if (type === 'document' || headerComponent?.parameters?.[0]?.type === 'document') {
      return (
        <div className="w-full h-[600px]">
          <embed
            src={mediaUrl}
            type={message.system_user_payload?.document?.mime_type}
            width="100%"
            height="100%"
            className="rounded-md shadow-lg"
          />
        </div>
      );
    }

    if (type === 'audio') {
      return (
        <audio className="w-full max-w-md" controls preload="metadata">
          <source src={mediaUrl} type="audio/mpeg" />
          Your browser does not support the audio element.
        </audio>
      );
    }
  };

  // Helper function to render the media section
  const renderMediaSection = () => {
    if (type === 'audio') {
      const mediaUrl = getMediaUrl();
      if (!mediaUrl) return null;
      return (
        <div className="flex min-w-0 w-full max-w-full flex-col items-end gap-2 rounded-md border border-solid border-neutral-border bg-white px-4 py-3 shadow-sm">
          <audio
            className="w-full max-w-[280px]"
            controls
            preload="metadata"
            src={mediaUrl}
          >
            <source src={mediaUrl} type="audio/mpeg" />
            Your browser does not support the audio element.
          </audio>
          <div className="flex w-full items-center justify-end gap-2">
            <SubframeCore.Icon
              className="text-body font-body text-subtext-color"
              name="FeatherMic"
            />
            <span className="text-caption font-caption text-subtext-color">
              Voice message
            </span>
          </div>
        </div>
      );
    }
    if (type === 'image' || type === 'video' || type === 'document' || headerComponent) {
      const mediaUrl = getMediaUrl();
      return (
        <div className="flex min-w-0 w-full max-w-full flex-col items-start border-b-2 border-solid border-neutral-border px-1 py-1 overflow-hidden">
          <div className="relative flex w-full flex-col items-center justify-center gap-2 rounded-t-md bg-neutral-200 px-1 py-1">
            <img
              className="h-36 w-full flex-none rounded-t-sm border-b-2 border-solid border-neutral-border object-cover blur-md"
              src={mediaUrl}
              alt="Message preview"
            />
            <button
              onClick={handlePreviewClick}
              title="Open preview"
              aria-label="Open preview"
              className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 p-2 rounded-full bg-white/80 backdrop-blur-md transition-transform duration-300 hover:scale-110 shadow-lg"
            >
              <SubframeCore.Icon
                className="text-heading-1 font-heading-1 text-neutral-600 p-50"
                name="FeatherArrowDownCircle"
              />
            </button>
          </div>

          {(type === 'document' || headerComponent?.parameters?.[0]?.type === 'document') && (
            <div className="flex min-w-0 w-full items-center gap-2 rounded-b-md bg-neutral-200 px-2 pt-1 pb-2.5">
              <SubframeCore.Icon
                className="text-heading-1 font-heading-1 shrink-0 text-default-font"
                name="FeatherFile"
              />
              <div className="flex min-w-0 flex-1 flex-col items-start">
                <span className="w-full break-words text-body-bold font-body-bold text-default-font">
                  Document
                </span>
                <span className="w-full break-all text-caption font-caption text-subtext-color">
                  {message.system_user_payload?.document?.mime_type?.split('/')[1]?.toUpperCase() || 'DOCUMENT'}
                </span>
              </div>
            </div>
          )}
        </div>
      );
    }
    return null;
  };

  // Helper function to render message content
  const renderMessageContent = () => (
    <div className="flex min-w-0 w-full max-w-full flex-col items-start gap-1 overflow-hidden px-4 pt-3 pb-1">
      {type !== 'audio' && (
        message.status === "failed" ? (
          <div className="flex items-center gap-2">
            <SubframeCore.Icon
              className="text-heading-1 font-heading-1 text-error-700"
              name="FeatherAlertTriangle"
            />
            <span className="w-full text-body font-body text-default-font">
              <MessageComponent message={message.system_user || ''} />
            </span>
          </div>
        ) : (
          <span className="w-full text-body font-body text-default-font">
            <MessageComponent message={message.system_user || ''} />
          </span>
        )
      )}
      <div className="flex w-full items-center justify-end gap-1">
        <span className="text-caption font-caption text-subtext-color">
          {moment(message.created_at).local().format("hh:mm A")}
          {message.send_by && (
            <>
              <span className="mx-1">•</span>
              <span>{message.send_by}</span>
            </>
          )}
        </span>
        {renderMessageTypeIndicator()}
        <SubframeCore.Icon
          className={`text-body font-body ${message.status === "read" ? "text-brand-500" : "text-subtext-color"
            }`}
          name={message.status === "delivered" || message.status === "read"
            ? "FeatherCheckCheck"
            : "FeatherCheck"}
        />
      </div>
    </div>
  );

  // Helper function to render message type indicator
  const renderMessageTypeIndicator = () => {
    if (type === 'video' || headerComponent?.parameters?.[0]?.type === 'video') {
      return (
        <div className="flex grow shrink-0 basis-0 items-center justify-end gap-1">
          <SubframeCore.Icon
            className="text-monospace-body font-monospace-body text-subtext-color"
            name="FeatherVideo"
          />
          <span className="text-caption font-caption text-subtext-color">
            Video message
          </span>
        </div>
      );
    }
    if (type === 'image' || headerComponent?.parameters?.[0]?.type === 'image') {
      return (
        <div className="flex grow shrink-0 basis-0 items-center justify-end gap-1">
          <SubframeCore.Icon
            className="text-monospace-body font-monospace-body text-subtext-color"
            name="FeatherImage"
          />
          <span className="text-caption font-caption text-subtext-color">
            Image message
          </span>
        </div>
      );
    }
    if (type === 'audio') {
      return (
        <div className="flex grow shrink-0 basis-0 items-center justify-end gap-1">
          <SubframeCore.Icon
            className="text-monospace-body font-monospace-body text-subtext-color"
            name="FeatherMic"
          />
          <span className="text-caption font-caption text-subtext-color">
            Voice message
          </span>
        </div>
      );
    }
    return null;
  };

  // Helper function to render template buttons
  const renderTemplateButtons = () => {
    if (!template?.components) return null;

    return template.components.map((component: any, idx: number) => {
      if (component.type === "button") {
        return (
          <div key={idx} className="flex min-w-0 w-full flex-col items-center justify-center gap-2 border-t-2 border-solid border-neutral-border px-1 py-1 overflow-hidden">
            <div className="flex min-w-0 w-full items-center justify-center gap-2">
              <SubframeCore.Icon
                className="text-body-bold font-body-bold text-[#10b989ff] shrink-0"
                name={component.sub_type === "url" ? "FeatherArrowUpRightSquare" :
                  component.sub_type === "VOICE_CALL" ? "FeatherPhone" :
                    component.sub_type === "quick_reply" ? "FeatherReply" : "FeatherCopy"}
              />
              <span className="font-['Nunito'] text-[14px] font-[600] leading-[20px] text-success-500 min-w-0 break-all">
                {component.parameters?.[0]?.text || component.parameters?.[0]?.payload || "Quick Reply"}
              </span>
            </div>
          </div>
        );
      }
      return null;
    });
  };

  if (message.type === 'location') {
    if (!message.system_user?.trim()) return null;

    return (
      <div ref={scrollRef} className="flex min-w-0 w-full max-w-full flex-col items-end gap-2 mt-2">
        <div className="flex min-w-0 max-w-[60%] flex-col items-end gap-2 overflow-hidden rounded-lg bg-white shadow-sm w-fit">
          <iframe
            width="400"
            height="300"
            className="max-w-full border-0"
            src={getLocationEmbedUrl(message.system_user.trim())}
            allowFullScreen
            title="Location"
          />
          <div className="flex w-full items-center justify-end gap-1 px-4 pb-3">
            <span className="text-caption font-caption text-subtext-color">
              {moment(message.created_at).local().format("hh:mm A")}
              {message.send_by && (
                <>
                  <span className="mx-1">•</span>
                  <span>{message.send_by}</span>
                </>
              )}
            </span>
            <SubframeCore.Icon
              className={`text-body font-body ${message.status === "read" ? "text-brand-500" : "text-subtext-color"}`}
              name={message.status === "delivered" || message.status === "read"
                ? "FeatherCheckCheck"
                : "FeatherCheck"}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div ref={scrollRef} className="flex min-w-0 w-full max-w-full flex-col items-end gap-2 mt-2">
        <div
          className={`flex min-w-0 max-w-[60%] flex-col items-end gap-2 overflow-hidden rounded-lg ${type === 'text' ? (message.status === 'failed' ? 'bg-error-200' : 'bg-green-100') : (message.status === 'failed' ? 'bg-error-200' : 'bg-white')
            } shadow-sm w-fit`}
        >
          {renderMediaSection()}
          {renderMessageContent()}
          {renderTemplateButtons()}
        </div>
      </div>

      <DialogLayout open={showPreview} onOpenChange={setShowPreview}>
        <div className="flex h-full w-full flex-col items-start gap-6 bg-default-background px-6 py-6">
          <div className="flex w-full items-center justify-between border-b border-solid border-neutral-border pb-4">
            <div className="flex grow shrink-0 basis-0 flex-col items-start gap-1">
              <span className="text-heading-2 font-heading-2 text-default-font">
                Media Preview
              </span>
            </div>
            <IconButton
              icon="FeatherX"
              onClick={() => setShowPreview(false)}
            />
          </div>

          <div className="relative w-full grow shrink-0 basis-0 flex-col items-center justify-center gap-4 rounded-md border border-solid border-neutral-border bg-neutral-50 px-6 py-6">
            <div className="flex flex-col items-center">
              {renderPreview()}
            </div>
          </div>
        </div>
      </DialogLayout>
    </>
  );
};

export default SystemUser;